import { memo } from 'react';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from 'src/components/ui/pagination';
import { DropdownMenuDemo } from 'src/components/Pagination/RowsPerPage';

export const PaginationComponent = memo(
  ({
    count = 0,
    currentPage,
    onPageChange,
    onRowsPerPageChange,
    rowsPerPageOptions,
    rowsPerPage,
    navigationLabel = false,
    disableRowsPerPage = false,
  }: {
    count?: number;
    currentPage: number;
    onPageChange: (page: number) => void;
    onRowsPerPageChange?: (value: number) => void;
    rowsPerPageOptions?: number[];
    rowsPerPage: number;
    navigationLabel?: boolean;
    disableRowsPerPage?: boolean;
  }) => {
    const totalPages = Math.ceil(count / rowsPerPage);

    const getPages = () => {
      const pages = [];

      if (totalPages <= 5) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push('...', totalPages);
      } else if (currentPage > totalPages - 4) {
        pages.push(1, '...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }

      return pages;
    };

    const pages = getPages();
    // An ellipsis is identified by the page it follows; there can be one on each side.
    const pageItems = pages.map((page, position) => ({
      page,
      key: page === '...' ? `ellipsis_after_${pages[position - 1]}` : `page_${page}`,
    }));

    return (
      <div className="flex items-center">
        {!disableRowsPerPage && (
          <DropdownMenuDemo
            onRowsPerPageChange={onRowsPerPageChange}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={rowsPerPageOptions}
          />
        )}
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                navigationLabel={navigationLabel}
                onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
              />
            </PaginationItem>
            {pageItems.map(({ page, key }) =>
              page === '...' ? (
                <PaginationItem key={key}>
                  <PaginationEllipsis />
                </PaginationItem>
              ) : (
                <PaginationItem key={key}>
                  <PaginationLink
                    isActive={page === currentPage}
                    // Only numeric pages reach here; '...' is rendered above.
                    onClick={() => onPageChange(page as number)}
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              ),
            )}
            <PaginationItem>
              <PaginationNext
                navigationLabel={navigationLabel}
                onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
                disabled={currentPage === totalPages}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    );
  },
);
